/* Feb-2026 · Retired Books admin panel.
 *
 * Lists rows with `retired_reason='orphan_auto_retire'`, newest first,
 * so ops can spot false positives and one-click republish. Powered by:
 *   GET  /api/admin/sync-report/retired?limit=50
 *   POST /api/admin/sync-report/republish/{bid}
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, PackageOpen, Undo2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { syncReportApi } from '@/lib/api';

function fmt(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const min = Math.round((Date.now() - d.getTime()) / 60000);
    if (min < 60) return `${min}m ago`;
    if (min < 60 * 24) return `${Math.round(min / 60)}h ago`;
    return `${Math.round(min / (60 * 24))}d ago`;
  } catch { return '—'; }
}

export default function RetiredBooksPanel() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [republishing, setRepublishing] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await syncReportApi.retired(50);
      setRows(r.books || []);
    } catch (e) {
      toast.error('Failed to load retired books.');
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onRepublish = async (b) => {
    if (!window.confirm(`Republish "${b.title}"?`)) return;
    setRepublishing(b.id);
    try {
      await syncReportApi.republish(b.id);
      toast.success(`Republished "${b.title}".`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Republish failed.');
    } finally { setRepublishing(null); }
  };

  return (
    <div className="rounded-md border border-border bg-card p-4 space-y-3"
         data-testid="retired-books-panel">
      <header className="flex items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-1.5 text-[12.5px] font-semibold">
            <PackageOpen className="h-4 w-4 text-primary" />
            Retired titles (auto-orphan) · {rows.length}
          </div>
          <div className="text-[11px] text-muted-foreground">
            Rows unpublished by the orphan sweeper. Click Republish to restore.
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={busy}
                data-testid="retired-books-refresh-btn"
                className="h-8 gap-1 text-[12px]">
          <RefreshCw className={`h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </header>
      {rows.length === 0 ? (
        <div className="rounded border border-dashed border-border bg-background/60 px-3 py-4 text-center text-[12px] text-muted-foreground">
          No auto-retired titles right now. Everything published is
          reaching the Companion Reader.
        </div>
      ) : (
        <table className="w-full text-[12px]">
          <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="pb-1 text-left">Title</th>
              <th className="pb-1 text-left">Author</th>
              <th className="pb-1 text-left">Genre</th>
              <th className="pb-1 text-right">Reads</th>
              <th className="pb-1 text-right">Retired</th>
              <th className="pb-1 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b.id} className="border-t border-border"
                  data-testid={`retired-row-${b.id}`}>
                <td className="py-1 pr-2">{b.title}</td>
                <td className="py-1 pr-2 text-muted-foreground">{b.author}</td>
                <td className="py-1 pr-2 text-[10.5px] uppercase tracking-wider text-muted-foreground">
                  {b.genre_slug || '—'}
                </td>
                <td className="py-1 pr-2 text-right font-mono">
                  {b.shelf_read_count ?? 0}
                </td>
                <td className="py-1 pr-2 text-right text-[10.5px] text-muted-foreground">
                  {fmt(b.retired_at)}
                </td>
                <td className="py-1 text-right">
                  <Button size="sm" variant="outline"
                          disabled={republishing === b.id}
                          onClick={() => onRepublish(b)}
                          className="h-7 gap-1 text-[11px]"
                          data-testid={`retired-republish-${b.id}`}>
                    {republishing === b.id
                      ? <Loader2 className="h-3 w-3 animate-spin" />
                      : <Undo2 className="h-3 w-3" />}
                    Republish
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
